import React from 'react';
import { formatDate } from '../../utils/dateFormat';

// Client entity classification and relationship — the frontend mirror of
// server/utils/clientEntity.js. Rendered by Settings → Clients & Entities,
// the New Client form / detail, Client Amendment and the Client Hub, so the
// three values, their labels and the UEN guidance are defined once here.
//
// Entity Category ≠ the engagement profile's PIE answer (Non-PIE / PIE /
// Government Entity…): that one classifies the engagement's public-interest
// status, this one says where and as what the company is registered. The
// two are answered independently.

export const ENTITY_CATEGORIES = ['local', 'government', 'overseas'];

export const ENTITY_CATEGORY_LABELS = {
  local: 'Singapore Company',
  government: 'Government Agency',
  overseas: 'Overseas Company',
};

// What goes in the UEN field for each category. The value itself is never
// format-checked — this is guidance, not a rule.
export const UEN_HINTS = {
  local: 'ACRA UEN as shown on the BizFile.',
  government: 'UEN of the ministry, statutory board or organ of state (uen.gov.sg).',
  overseas: 'Registration number issued in the country of incorporation. A registered Singapore branch should be entered as a Singapore Company with its branch UEN.',
};

export const entityCategoryLabel = (key) => ENTITY_CATEGORY_LABELS[key] || key || '—';
export const uenHintFor = (key) => UEN_HINTS[key] || 'Pick the Entity Category to see what to enter here.';

// Only a Singapore Company has an ACRA BizFile; the other two may attach
// their registration document, nothing forces it.
export const bizFileRequired = (entityCategory) => (entityCategory || 'local') === 'local';

/** The three-way select. `value` '' = not chosen yet. */
export const EntityCategorySelect = ({ value, onChange, className, disabled = false }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={className} disabled={disabled}>
    <option value="">Select…</option>
    {ENTITY_CATEGORIES.map((k) => <option key={k} value={k}>{ENTITY_CATEGORY_LABELS[k]}</option>)}
  </select>
);

const CATEGORY_CHIP_CLS = {
  local: 'bg-slate-100 text-slate-700',
  government: 'bg-violet-50 text-violet-700',
  overseas: 'bg-sky-50 text-sky-700',
};

/** Read-only chip naming the category. */
export const EntityCategoryChip = ({ value, className = '' }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_CHIP_CLS[value] || 'bg-slate-100 text-slate-600'} ${className}`}>
    {entityCategoryLabel(value)}
  </span>
);

/**
 * Relationship badge — Active, or Inactive with the date. `entity` is any
 * object carrying `active` and (when inactive) `inactiveDate`.
 */
export const RelationshipBadge = ({ entity, className = '' }) => (
  entity && entity.active === false
    ? (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600 ${className}`}>
        Inactive{entity.inactiveDate ? ` · ${formatDate(entity.inactiveDate)}` : ''}
      </span>
    )
    : <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 ${className}`}>Active</span>
);
